import { Pipe, PipeTransform, inject } from '@angular/core';
import { DatePipe } from '@angular/common';
import { UserSessionService } from '../../services/user-session.service';
import { TimeZoneUtilities } from '../../utilities/timezone.utilities';

// Matches ISO strings whose time portion is zero (or absent). These are calendar dates
// regardless of how the backend serialized them (DateOnly, UTC-kind midnight, local-kind midnight)
// and must not be shifted by timezone conversion or they slip a day.
//
// Accepts: 2026-04-15, 2026-04-15T00:00:00, 2026-04-15T00:00:00.000, 2026-04-15T00:00:00Z,
//          2026-04-15T00:00:00.000Z, 2026-04-15T00:00:00+00:00, 2026-04-15T00:00:00-00:00
const CALENDAR_DATE_REGEX = /^(\d{4})-(\d{2})-(\d{2})(?:T00:00:00(?:\.0+)?(?:Z|[+-]00:00)?)?$/;

// Matches ISO datetime strings that have no timezone designator. JavaScript's Date constructor
// interprets these as local time, but the backend stores everything in UTC — so we treat any
// naked ISO datetime as UTC by appending "Z". Strings that already carry "Z" or a "+HH:MM" /
// "-HH:MM" offset are left alone so the API can keep sending properly-tagged timestamps.
const NAKED_ISO_DATETIME_REGEX = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?$/;

@Pipe({
  name: 'orgDate',
  pure: false,
})
export class OrgDatePipe implements PipeTransform {

  private userSessionService = inject(UserSessionService);
  private datePipe = new DatePipe('en-US');
  private offsetFormatters = new Map<string, Intl.DateTimeFormat>();

  public transform(value: string | Date | null | undefined, format: string = 'shortDate'): string | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    if (typeof value === 'string') {
      const calendarDate = this.parseCalendarOnly(value);
      if (calendarDate) {
        return this.datePipe.transform(calendarDate, format);
      }

      if (NAKED_ISO_DATETIME_REGEX.test(value)) {
        value = value + 'Z';
      }
    }

    const timeZoneId = this.userSessionService.activeOrganizationSettings()?.timeZoneId;
    const ianaTimeZone = TimeZoneUtilities.toIanaTimeZone(timeZoneId);

    // Angular's DatePipe only honors timezone offset strings (e.g. "-0500"); it silently
    // falls back to the browser's local zone when given an IANA name like "America/Adak".
    // Compute a DST-aware offset for this specific instant and pass that instead.
    const offset = ianaTimeZone ? this.computeOffsetForInstant(value, ianaTimeZone) : undefined;

    return this.datePipe.transform(value, format, offset);
  }

  private parseCalendarOnly(value: string): Date | null {
    const match = CALENDAR_DATE_REGEX.exec(value);
    if (!match) return null;

    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);

    return new Date(year, month - 1, day);
  }

  private computeOffsetForInstant(value: string | Date, ianaTimeZone: string): string | undefined {
    const date = typeof value === 'string' ? new Date(value) : value;
    if (isNaN(date.getTime())) return undefined;

    let formatter = this.offsetFormatters.get(ianaTimeZone);
    if (!formatter) {
      formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: ianaTimeZone,
        timeZoneName: 'longOffset',
      });
      this.offsetFormatters.set(ianaTimeZone, formatter);
    }

    const tzPart = formatter.formatToParts(date).find(p => p.type === 'timeZoneName');

    if (!tzPart) return undefined;
    if (tzPart.value === 'GMT') return '+0000';

    // Intl returns "GMT-10:00", "GMT+05:30", etc. DatePipe wants "-1000", "+0530".
    const match = tzPart.value.match(/GMT([+-])(\d{1,2})(?::(\d{2}))?/);
    if (!match) return undefined;

    const sign = match[1];
    const hours = match[2].padStart(2, '0');
    const minutes = (match[3] ?? '00').padStart(2, '0');
    return `${sign}${hours}${minutes}`;
  }
}
